import { Suspense, useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useController, useFieldArray } from 'react-hook-form';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
// eslint-disable-next-line import/no-extraneous-dependencies -- cyclic workspace dep: metrics → maintainer → ... → metrics
import { MaintainerDialog } from '@gonfalon/maintainer/internal';
import { MemberMaintainerTag } from '@gonfalon/members/MemberMaintainerTag';
import { TagAutocomplete } from '@gonfalon/tags/components/TagAutocomplete';
import {
  Alert,
  Button,
  Dialog,
  DialogTrigger,
  IconButton,
  ListBoxItem,
  Popover,
  ProgressBar,
  TagGroup,
  TagList,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import type { MetricForm } from './formUtils';

import styles from './MetricFooterFields.module.css';

type MetricFooterFieldsProps = {
  context: UseFormReturn<MetricForm>;
  isMaintainerOptional?: boolean;
};

type MaintainerPopoverContentProps = {
  maintainerId: string;
  onSelect: (key: string | undefined) => void;
};

const MaintainerPopoverContent = ({ maintainerId, onSelect }: MaintainerPopoverContentProps) => (
  <div className={styles.popoverContainer}>
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          severity="medium"
          onReset={reset}
          fallbackRender={({ resetErrorBoundary }) => (
            <Alert variant="inline" status="error">
              Failed to load maintainers
              <Button size="small" onPress={() => resetErrorBoundary()}>
                Try again
              </Button>
            </Alert>
          )}
        >
          <Suspense
            fallback={
              <div className={styles.maintainerLoadingContainer}>
                <ProgressBar aria-label="Loading…" isIndeterminate size="small" />
              </div>
            }
          >
            <MaintainerDialog
              selectionMode="single"
              maintainerId={maintainerId || undefined}
              onMemberSelectionChange={onSelect}
              membersOnly
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  </div>
);

type MaintainerFieldProps = {
  maintainerId: string;
  isMaintainerOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onRemove: () => void;
  onSelect: (key: string | undefined) => void;
};

const MaintainerField = ({
  maintainerId,
  isMaintainerOpen,
  onOpenChange,
  onRemove,
  onSelect,
}: MaintainerFieldProps) => (
  <ErrorBoundary
    severity="medium"
    fallbackRender={() => (
      <Button size="small" isDisabled>
        <Icon name="person" size="small" />
      </Button>
    )}
  >
    {maintainerId ? (
      <DialogTrigger isOpen={isMaintainerOpen} onOpenChange={onOpenChange}>
        <TagGroup aria-label="Maintainer" selectionMode="multiple" onRemove={onRemove}>
          <TagList>
            <MemberMaintainerTag
              className={styles.maintainerButton}
              maintainerId={maintainerId}
              id="maintainer"
              textValue={`member:${maintainerId}`}
            />
          </TagList>
        </TagGroup>
        <Popover placement="bottom start">
          <MaintainerPopoverContent maintainerId={maintainerId} onSelect={onSelect} />
        </Popover>
      </DialogTrigger>
    ) : (
      <DialogTrigger isOpen={isMaintainerOpen} onOpenChange={onOpenChange}>
        <TooltipTrigger>
          <Button size="small" aria-label="Add maintainer">
            <Icon name="person" size="small" />
          </Button>
          <Tooltip>Add maintainer</Tooltip>
        </TooltipTrigger>
        <Popover placement="bottom start">
          <MaintainerPopoverContent maintainerId={maintainerId} onSelect={onSelect} />
        </Popover>
      </DialogTrigger>
    )}
  </ErrorBoundary>
);

type TagsFieldProps = {
  tagFields: Array<{ id: string; value: string }>;
  replaceTags: (tags: Array<{ label: string; value: string }>) => void;
};

const TagsField = ({ tagFields, replaceTags }: TagsFieldProps) => {
  const tagAutocomplete = (
    <TagAutocomplete
      allowCreate
      kind="metric"
      selectedKeys={tagFields.map((field) => field.value)}
      onSelectionChange={(selection) => {
        if (selection !== 'all') {
          const selectedArray = Array.from(selection).map((key) => String(key));
          replaceTags(selectedArray.map((value) => ({ label: value, value })));
        }
      }}
    >
      {(item) => <ListBoxItem id={item.id}>{item.name}</ListBoxItem>}
    </TagAutocomplete>
  );

  if (tagFields.length > 0) {
    return (
      <DialogTrigger>
        <TooltipTrigger>
          <Button size="small">
            <Icon name="tag" size="small" />
            {tagFields.length} {tagFields.length === 1 ? 'tag' : 'tags'}
          </Button>
          <Tooltip>Manage tags</Tooltip>
        </TooltipTrigger>
        <Popover placement="bottom start">
          <Dialog className={styles.tagPopoverContainer}>{tagAutocomplete}</Dialog>
        </Popover>
      </DialogTrigger>
    );
  }

  return (
    <DialogTrigger>
      <TooltipTrigger>
        <IconButton icon="tag" size="small" aria-label="Add tag" />
        <Tooltip>Add tag</Tooltip>
      </TooltipTrigger>
      <Popover placement="bottom start">
        <Dialog className={styles.tagPopoverContainer}>{tagAutocomplete}</Dialog>
      </Popover>
    </DialogTrigger>
  );
};

export const MetricFooterFields: React.FC<MetricFooterFieldsProps> = ({ context, isMaintainerOptional = false }) => {
  const [isMaintainerOpen, setIsMaintainerOpen] = useState(false);
  const { setError } = context;

  const { field: maintainerField } = useController({
    name: 'maintainerId',
    control: context.control,
    rules: isMaintainerOptional ? {} : { required: 'Maintainer is required' },
  });

  const { fields: tagFields, replace: replaceTags } = useFieldArray({
    control: context.control,
    name: 'tags',
  });

  useEffect(() => {
    if (!isMaintainerOptional && !maintainerField.value) {
      setError('maintainerId', { message: 'Maintainer is required' });
    }
  }, [isMaintainerOptional, maintainerField.value, setError]);

  const handleMaintainerSelect = (key: string | undefined) => {
    context.setValue('maintainerId', key ?? '', { shouldValidate: true, shouldDirty: true });
    setIsMaintainerOpen(false);
  };

  const handleMaintainerRemove = () => {
    context.setValue('maintainerId', '', { shouldValidate: true, shouldDirty: true });
    setIsMaintainerOpen(false);
  };

  return (
    <div className={styles.footerFields}>
      <MaintainerField
        maintainerId={maintainerField.value}
        isMaintainerOpen={isMaintainerOpen}
        onOpenChange={setIsMaintainerOpen}
        onRemove={handleMaintainerRemove}
        onSelect={handleMaintainerSelect}
      />
      <TagsField tagFields={tagFields} replaceTags={replaceTags} />
    </div>
  );
};
